import React, { useEffect, useState, useRef, useMemo } from 'react'
import { Grid, TextField, FormHelperText, MenuItem } from '@mui/material'
import DOBcalc from './DOBcalc'
import dayjs from 'dayjs'
import countries from '../DATA_SET/countries.json'

/**
 * PassportInfo
 * Props:
 *  - value: { passport_number, passport_issue_country, passport_expiry }
 *    (dates as 'YYYY-MM-DD' or null; issuing country as ISO country name, e.g. 'India')
 *  - onChange: function called with same object + { valid }
 *  - required: boolean, show simple required validation
 */
export default function PassportInfo({ value = {}, onChange, required = false }) {
  const [passportNumber, setPassportNumber] = useState(value?.passport_number || '')
  // default to India (IN) when not provided
  const [issuingCountry, setIssuingCountry] = useState(value?.passport_issue_country || 'India')
  const [expiry, setExpiry] = useState(value.passport_expiry || '')
  const [touched, setTouched] = useState({ number: false, issuingCountry: false, expiry: false })

  console.log('PassportInfo value:', value)
  useEffect(() => {
    // reflect external value changes
    setPassportNumber(value.passport_number || '')
    // reflect issuing country from external value (default to IN)
    setIssuingCountry(value?.passport_issue_country || 'India')
    if (value?.passport_expiry) {
      const d2 = parseDate(value.passport_expiry)
      setExpiry(d2 ? d2.format('DD-MM-YYYY') : value.passport_expiry)
    } else {
      setExpiry('')
    }
  }, [value.passport_number, value.passport_issue_country, value.passport_expiry])

  const countryOptions = useMemo(() => {
    try {
      return countries.slice().sort((a, b) => a.name.localeCompare(b.name))
    } catch (e) {
      return countries || []
    }
  }, [])

  const parseDate = (v) => {
    if (!v) return null
    // Accept ISO (YYYY-MM-DD) or DD/MM/YYYY user display
    let d = dayjs(v, 'YYYY-MM-DD', true)
    if (d.isValid()) return d
    d = dayjs(v, 'DD/MM/YYYY', true)
    if (d.isValid()) return d
    d = dayjs(v, 'DD-MM-YYYY', true)
    if (d.isValid()) return d
    // fallback to loose parse
    d = dayjs(v)
    return d.isValid() ? d : null
  }

  // using DOBcalc selects for issue/expiry inputs (DD/MM/YYYY style selects)

  const expiryDate = parseDate(expiry)

  const numberError = required && touched.number && !passportNumber
  const issuingCountryError = required && touched.issuingCountry && !issuingCountry
  const expiryError = required && touched.expiry && !expiryDate

  useEffect(() => {
    const valid = !numberError && !issuingCountryError && !expiryError
    if (typeof onChange === 'function') {
      // convert display DD/MM/YYYY to ISO if possible
      const expiryIso = expiry ? (parseDate(expiry) ? parseDate(expiry).format('YYYY-MM-DD') : null) : null
      onChange({ passport_number: passportNumber || null, passport_issue_country: issuingCountry || null, passport_expiry: expiryIso, valid })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passportNumber, issuingCountry, expiry, numberError, issuingCountryError, expiryError])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <div className='label' >Passport Number</div>
        <TextField
          InputProps={{ sx: { height: '3rem' } }}

          // label="Passport No"
          placeholder="Passport No"
          fullWidth
          value={passportNumber}
          onChange={(e) => setPassportNumber(e.target.value)}
          onBlur={() => setTouched((s) => ({ ...s, number: true }))}
          error={!!numberError}
          helperText={numberError ? 'Passport number is required' : ''}
        />
      </Grid>
 <Grid item xs={12} md={5}>
        <div className="label">Passport Expiry(DD-MM-YYYY)</div>
        <DOBcalc
          value={expiry ? (parseDate(expiry) ? parseDate(expiry).format('YYYY-MM-DD') : undefined) : undefined}
          onChange={(payload) => {
            const iso = payload.iso
            setExpiry(iso ? dayjs(iso).format('DD-MM-YYYY') : '')
            setTouched((s) => ({ ...s, expiry: true }))
          }}
          required={required}
          minDate={dayjs().format('YYYY-MM-DD')}
          maxDate={dayjs().add(50, 'year').format('YYYY-MM-DD')}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <div className="label">Issuing Country</div>
        <TextField
          select
          fullWidth
          value={issuingCountry}
          onChange={(e) => {
            setIssuingCountry(e.target.value)
            setTouched((s) => ({ ...s, issuingCountry: true }))
          }}
          onBlur={() => setTouched((s) => ({ ...s, issuingCountry: true }))}
          error={!!issuingCountryError}
          helperText={issuingCountryError ? 'Issuing country is required' : ''}
          InputProps={{ sx: { height: '3rem' } }}
        >
          <MenuItem value="">Select country</MenuItem>
          {countryOptions.map((c) => (
            <MenuItem key={c.name} value={c.name}>{`${c.name} (${c.name})`}</MenuItem>
          ))}
        </TextField>
      </Grid>

     

      {/* no range validation (issue date removed) */}
    </Grid>
  )
}
